import { Crown, Eye, Shield, User as UserIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useImpersonation, type ImpersonationRole } from "@/contexts/ImpersonationContext";

type RoleMeta = {
  label: string;
  short: string;
  description: string;
  icon: typeof UserIcon;
  tone: string;
};

const ROLE_META: Record<Exclude<ImpersonationRole, null>, RoleMeta> = {
  anonymous: {
    label: "Ziyaretçi",
    short: "Ziyaretçi",
    description: "Giriş yapmamış anonim ziyaretçi. Sadece public sayfalar; üye/premium içerik tıklanırsa popup ile Premium'a yönlendirilir.",
    icon: Eye,
    tone: "border-muted-foreground/40 bg-background/95 text-muted-foreground",
  },
  guest_member: {
    label: "Misafir Üye",
    short: "Misafir Üye",
    description: "Mailiyle kayıt olmuş ücretsiz üye. Free içeriklere erişir, premium içerikler kilitlidir.",
    icon: UserIcon,
    tone: "border-sky-500/40 bg-sky-500/10 text-sky-500",
  },
  premium: {
    label: "Strength in Range Üyesi",
    short: "Strength in Range",
    description: "Ücretli paket sahibi. Tüm premium içerik, randevu, AI analiz ve destek erişimi açık.",
    icon: Crown,
    tone: "border-primary/50 bg-primary/15 text-primary",
  },
};

const ImpersonationBadge = () => {
  const { realIsAdmin, isImpersonating, role, status, endsInDays } = useImpersonation();

  if (!realIsAdmin || !isImpersonating || !role) return null;

  const meta = ROLE_META[role];
  const Icon = meta.icon;
  const showStatus = role !== "anonymous";
  const statusLabel =
    status === "expired" ? "Bitti" : status === "canceled" ? "İptal" : "Aktif";
  const statusTone =
    status === "expired"
      ? "bg-destructive/15 text-destructive"
      : status === "canceled"
      ? "bg-amber-500/15 text-amber-500"
      : "bg-emerald-500/10 text-emerald-500";
  const endsLabel =
    endsInDays === 0 ? "bugün" : endsInDays > 0 ? `${endsInDays}g sonra` : `${Math.abs(endsInDays)}g önce`;

  return (
    <AnimatePresence>
      <motion.div
        key="impersonation-badge"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        className="fixed top-2 right-2 z-[60] pointer-events-auto"
      >
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`inline-flex items-center gap-1.5 rounded-full border backdrop-blur-md px-2.5 py-1 shadow-md ${meta.tone}`}
                role="status"
                aria-label={`Test modu: ${meta.label}`}
              >
                <Shield size={11} className="opacity-80" />
                <Icon size={12} />
                <span className="text-[10px] sm:text-xs font-semibold tracking-wide">
                  {meta.short}
                </span>
                {showStatus && (
                  <span
                    className={`hidden sm:inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${statusTone}`}
                  >
                    {statusLabel} · {endsLabel}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-[240px]">
              <div className="space-y-1">
                <p className="text-xs font-semibold">Test Modu · {meta.label}</p>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {meta.description}
                </p>
                {showStatus && (
                  <p className="text-[11px] text-muted-foreground">
                    Abonelik: <span className="font-medium">{statusLabel}</span> · bitiş {endsLabel}
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImpersonationBadge;
